import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/dc";
import { useAuth } from "../auth/AuthContext";
import usePageTitle from "../hooks/usePageTitle";
import { apiGet } from "../api";
import { formatCurrency, formatNumber } from "../lib/format";
import { MODULES } from "../modules";

/**
 * ProcOps home — the platform module launcher. One card per module, sourced from
 * the module registry (src/modules.js), so new modules appear here automatically.
 * Each card shows a couple of live headline metrics (module.metrics in the
 * registry). The grid scales from 2 to N columns as modules are added.
 */
const _get = (o, p) => p.split(".").reduce((a, k) => (a == null ? a : a[k]), o);
const _fmt = (v, fmt) => {
  if (v == null) return "—";
  if (fmt === "gbp0") return formatCurrency(v, { dp: 0 });
  if (fmt === "pct") return `${v}%`;
  return formatNumber(v);
};
export default function Landing() {
  const { user } = useAuth();
  usePageTitle("Procurement Operations");
  const first = user?.name?.split(" ")[0];
  // Grid auto-fits as many ~280px columns as fit, then wraps, so it holds up
  // from a wide desktop down to a phone without a fixed column count.
  const [metrics, setMetrics] = useState({}); // module.key → endpoint response

  useEffect(() => {
    MODULES.forEach((m) => {
      if (!m.metrics) return;
      apiGet(m.metrics.endpoint)
        .then((d) => { if (d) setMetrics((prev) => ({ ...prev, [m.key]: d })); })
        .catch(() => {});
    });
  }, []);

  return (
    <div data-testid="landing-page">
      <PageHeader
        title="Procurement Operations"
        subtitle={first ? `Welcome back, ${first} — select a module to continue.` : "Select a module to continue."}
      />
      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, 280px), 1fr))`, gap: 24, marginTop: 8 }}>
        {MODULES.map((p) => (
          <Link
            key={p.key}
            to={p.entry}
            data-testid={`landing-${p.key}`}
            style={{ textDecoration: "none" }}
          >
            <div
              className="dc-landing-card"
              style={{
                background: "var(--color-bg-hemocyanin)",
                border: "1px solid var(--hairline)",
                borderRadius: 18,
                padding: 30,
                minHeight: 300,
                display: "flex",
                flexDirection: "column",
                transition: "transform .12s ease, border-color .12s ease",
              }}
            >
              {p.metrics && (
                <div style={{ display: "flex", gap: 28, marginBottom: 22 }}>
                  {p.metrics.items.map((it) => (
                    <div key={it.key}>
                      <div style={{ fontSize: 10.5, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--color-text-4)" }}>{it.label}</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: p.accent, marginTop: 3, minHeight: 27 }}>
                        {metrics[p.key]
                          ? _fmt(_get(metrics[p.key], it.key), it.fmt)
                          : <span className="dc-skeleton" style={{ display: "inline-block", width: 48, height: 18, borderRadius: 6, marginTop: 4 }} aria-hidden="true" />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div
                style={{
                  width: 54, height: 54, borderRadius: 14, marginBottom: 20,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 26, fontWeight: 700, color: "#0c0818",
                  background: p.accent,
                }}
                aria-hidden="true"
              >
                {p.mark}
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 700, color: "var(--color-text-1)" }}>
                {p.name}
              </div>
              <div style={{ fontSize: 14, color: p.accent, fontWeight: 600, marginTop: 4 }}>{p.tagline}</div>
              <div style={{ fontSize: 14, color: "var(--color-text-3)", marginTop: 14, lineHeight: 1.5 }}>{p.desc}</div>
              <div style={{ marginTop: "auto", paddingTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
                {p.points.map((pt) => (
                  <div key={pt} style={{ fontSize: 13, color: "var(--color-text-2)", display: "flex", alignItems: "center", gap: 9 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: p.accent, display: "inline-block" }} />
                    {pt}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 22, fontSize: 14, fontWeight: 700, color: p.accent }}>
                Open {p.name} →
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
